import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Carrier } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { driverKeys } from '../../drivers/api';
import { vehicleKeys } from '../../vehicles/api';
import { carrierKeys, createCarrierApi } from '../api';
import type { EnableOnSiteRequest } from '../api/carrier.api';

export function useEnableOnSite(): UseMutationResult<Carrier, Error, EnableOnSiteRequest> {
  const client = useApiClient('admin-auth');
  const api = createCarrierApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<Carrier, Error, EnableOnSiteRequest>({
    mutationFn: (request: EnableOnSiteRequest) => api.enableOnSite(request),
    onError: () => {
      toast.error(t('sites.settings.carriers.enable.error'));
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: carrierKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: carrierKeys.detail(variables.carrierId) });
      // Enabling changes server-side visibility of drivers and vehicles for this scope.
      void queryClient.invalidateQueries({ queryKey: driverKeys.all });
      void queryClient.invalidateQueries({ queryKey: vehicleKeys.all });
      toast.success(t('sites.settings.carriers.enable.success'));
    },
  });
}
